import { getApiClient } from '../services/api';
import { PaginationParams } from '../types/base';
import { IRightsType, USER_ADMIN } from '../types/enums';
import type { VerifyRejectReason } from '../types/verify';
import { IRank } from './storage';

// ============================================================================
// 枚举类型
// ============================================================================

/** 用户认证状态 */
export enum USER_VERIFY_STATUS {
  WAIT = 0, // 待审核
  SUCCESS = 1, // 成功
  FAIL = 2, // 失败
  CANCEL = 3, // 撤销
  RESEND = 4, // 重新认证
}

// 分页响应接口
export interface PaginatedResponse<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

/** 微信扫码状态 */
export enum IScanStatus {
  FAIL = 0,
  SUCCESS = 1,
  PENDING = 2,
}

/** 组织类型 */
export enum ORGANIZATION_TYPE {
  TEAM_MEMBER = 0, // 团队成员
  MODULE_ENTERPRISE_MEMBER = 1, // 模块企业成员
  PERSON = 2, // 个人企业成员（即个人账号）
}

// ============================================================================
// 用户权限相关接口
// ============================================================================

/** 渲染视频选项接口 */
export interface RendingVideoOptions {
  '540P': number;
  '1080P': number;
  '2K': number;
  '4K': number;
  '720P': number;
}

/** 限制大小接口 */
export interface LimitMegabyte {
  font_limit: number;
  audio_limit: number;
  image_limit: number;
  video_limit: number;
  ppt_limit: number;
  pdf_limit: number;
}

/** 用户权限信息接口 */
export interface UserRights {
  credit_coin: number;
  rendering_channel_num: number;
  total_sub_account_num: number;
  total_credit_coin: number;
  total_storage: number;
  total_authorization_identify_num: number;
  authorization_identify_num: number;
  video_export_count_limit: number;
  rights_video_export_count_limit: number;
  sub_account_num: number;
  storage: number;
  total_used_storage: number;
}

/** 权限记录接口 */
export interface RightsRecord {
  id: number;
  render_pass: number;
  speed: number;
  max_resolution: number;
  file_upload_type: string;
  video_download_type: string;
  watermark: number;
  recycle: number;
  single_video_max_duration: number;
  publish: number;
  team_work: number;
  one_coin_time: number;
  speed_up_coin: number;
  font_limit: number;
  audio_limit: number;
  image_limit: number;
  video_limit: number;
  ppt_limit: number;
  pdf_limit: number;
  credit_coin_time: number;
  time_identity: string;
  rending_video_options: RendingVideoOptions;
  allow_add_coin: boolean;
  allow_add_storage: boolean;
  allow_add_account: boolean;
  rights_type: number;
  is_supported_upload_logo: boolean;
  video_export_count_limit: number;
  limit_megabyte: LimitMegabyte;
  voice_loning_generate_count_limit: number;
  online_room_use_time_limit: number;
  ppt_conversion_count_limit: number;
  img2img_generate_count_limit: number;
}

// ============================================================================
// 用户认证相关接口
// ============================================================================

/** 身份信息VO */
export interface IdentityInfoVO {
  authorization_img: string;
  identify_img: string;
  legal_person_name: string;
  legal_person_phone: string;
  status: USER_VERIFY_STATUS;
  name: string;
}

/** 身份信息 */
export interface IdentifyInfo {
  name: string;
  legal_person_name: string;
  legal_person_phone: string;
  finished_time: string;
  legal_person_email: string;
  reject_reason: VerifyRejectReason[];
  status: USER_VERIFY_STATUS;
}

/** 身份信息返回结果 */
export interface IdentifyInfoResult {
  /**
   * 是否通过认证
   */
  is_identify: boolean;
  /**
   * 当前最新的认证信息
   */
  identify_info: IdentifyInfo;
}

/** 账号身份信息 */
export interface AccountIdentifyInfo {
  id: number;
  reject_reason: string[];
  identify_img: string;
  authorization_img: string | null;
  organization_type: number;
  name: string;
  user_id: number;
  admin_id: number;
  organization_id: number;
  identify_no: string;
  contact_no: string;
  identify_type: number;
  legal_person_name: string | null;
  legal_person_identify: string | null;
  legal_person_phone: string | null;
  legal_person_email: string | null;
  legal_person_img: string | null;
  status: number;
  create_time: string;
  finished_time: string | null;
  update_time: string;
}

// ============================================================================
// 用户信息相关接口
// ============================================================================

/** 用户信息接口 */
export interface UserInfoVO {
  status: number;
  has_independent_copyright: boolean;
  is_kicked_user: boolean;
  account_id: number;
  ma_info_id: number;
  account_name: string;
  username: string;
  openid: string;
  feishu_openid: string | null;
  create_time: string;
  update_time: string;
  organization_name: string;
  organization_create_time: string;
  organization_admin_account_id: number;
  /** 用户等级 */
  permission: USER_ADMIN;
  user_id: number;
  organization_id: number;
  logo_path: string | null;
  logo_url: string;
  default_logo_url: string;
  phone_number: string;
  email_address: string | null;
  organization_type: number;
  contact_details: string;
  start_time: string | null;
  end_time: string | null;
  rights: UserRights;
  expired_time: string;
  odd_sub_account_seat_count: number;
  current_package_expired_time: string | null;
  is_expired: boolean;
  rights_type: IRightsType;
  organization_admin_user: boolean;
  is_available_first_buy: boolean;
  current_order_id: number;
  rights_name: string;
  rights_record: RightsRecord;
  already_used_rights: {
    already_used_clone_vcn_count: number;
    already_used_image_to_char_count: number;
    already_used_online_time: number;
    already_used_ppt_count: number;
    is_subscription: boolean; // 当前用户是否是续费状态
  };
  login_name: string;
  used_credit_coins: number;
  personal_expired_time: string;
  platform_logo_url: string;
  default_platform_logo_url: string;
  is_edit_logo: boolean;
  profile_url: string;
  current_total_credit_coin: number;
  current_remained_credit_coin: number;
  account_total_credit_coin: number;
  account_remained_credit_coin: number;
  current_buy_more_total_credit_coin: number;
  current_buy_more_remained_credit_coin: number;
  account_buy_more_total_credit_coin: number;
  account_buy_more_remained_credit_coin: number;
  free_account_total_credit_coin: number;
  apply_credit_coin_expired_time: string;
  free_account_remained_credit_coin: number;
  current_activity_total_credit_coin: number;
  current_activity_remained_credit_coin: number;
  account_activity_total_credit_coin: number;
  account_activity_remained_credit_coin: number;
  rights_time_identity: string;
  rights_id: number;
  is_identify: boolean;
  member_can_visible_pay_view_page: boolean;
  identify_info: IdentityInfoVO;
  config_type: number;
}

/** 账号信息 */
export interface Account {
  id: number;
  openid: string | null;
  feishu_openid: string | null;
  org_name: string;
  username: string;
  phone_number: string;
  email_address: string | null;
  name: string;
  permission: number;
  custom_org_name: string | null;
  logo_path: string | null;
  logo_url: string;
  create_time: string;
  is_expired: boolean;
  expired_time: string;
  is_kicked_user: boolean;
  update_time: string;
  user_id: number;
  organization_id: number;
  organization_type: number;
  status: number;
  remark: string;
  start_time: string | null;
  end_time: string | null;
  rights_type: IRightsType;
  login_name: string;
  used_credit_coins: number;
  platform_logo_url: string;
  is_edit_logo: boolean;
  ma_info_id: number;
  project_authorization_status: number;
  supplier: string | null;
  is_active_user: boolean;
  is_identify: boolean;
  identify_info: AccountIdentifyInfo;
}

export interface ChangePhoneRequestData {
  mobile?: string;
  sms_code: string;
  email?: string;
}

export interface changePasswordLoginData {
  rsa_key_id: number;
  auth_data: string;
  password1: string;
  password2: string;
  mobile: string;
  sms_code: string;
}

export interface changePasswordEmailData {
  rsa_key_id: number;
  auth_data: string;
  email: string;
  verify_code: string;
  password: string;
}

// ============================================================================
// 登录相关接口
// ============================================================================

/** 会话信息 */
export interface Session {
  key: string;
  expire_time: string;
  expire_in: number;
  session_id: string;
  user_id: number;
}

/** 账户信息附带会话字段 */
export type AccountWithSession = Account & Session;

/** UTM追踪参数 */
export interface UTMRequestData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

/** 账号登录数据 */
interface AccountLoginData extends Partial<UTMRequestData> {
  rsa_key_id: number;
  auth_data: string;
  source?: string;
}

/** 邮箱登录请求数据 */
export interface LoginEmailRequestData extends UTMRequestData {
  rsa_key_id: number;
  auth_data: string;
  source?: string;
  country?: string;
}

/** 登录信息 */
export interface LoginInfo extends Account {
  key: string;
  expire_in: number;
  expire_time: string;
  account_name: string;
  is_new_user?: boolean;
  invitation_info?: {
    user_id: number;
    organization_id: string;
  };
  session_id: string;
}

/** RSA公钥信息 */
export interface UserRsaPublicKey {
  public_key: string;
  id: number;
}

// ============================================================================
// 微信相关接口
// ============================================================================

/** 微信公众号扫码状态 */
export interface IWechatOfficialAccountScanStatus {
  status: IScanStatus;
  action: 'login' | 'register';
  login_data: Account[];
}

/** 微信公众号票据 */
export interface IWechatOfficialAccountTicket {
  expired_in: number;
  ticket: string;
  ticket_id: string;
  url: string;
}

// ============================================================================
// 团队成员相关接口
// ============================================================================

/** 获取组织账号参数 */
export interface IGetOrganizationAccounts extends Partial<PaginationParams> {
  name_or_mobile?: boolean;
  many?: number;
  ignore_kicked?: boolean;
  is_active_user: number | 0 | 1 | 2; // 0:禁用，1：启用，2:全部
}

/** 团队成员信息 */
export interface Member {
  account_id: number;
  account_name: string;
  create_time: string;
  email_address: string;
  is_active_user: boolean;
  is_kicked_user: boolean;
  logo_url: string;
  permission: number;
  phone_number: string;
  used_credit_coins: number;
  used_storage: number;
  user_id: number;
  username: string;
}

/** 编辑团队成员账号 */
export interface EditTeamMemberAccount {
  id: number;
  name?: string;
  rsa_key_id?: number;
  auth_data?: string;
}

/** 添加团队成员账号*/
export interface TeamMemberAccount {
  rsa_key_id: number;
  name: string;
  login_name: string;
  login_password?: string;
  auth_data?: string;
}

/** 踢出团队成员参数 */
export interface KickMemberParams {
  user_id?: string;
  [key: string]: unknown;
}

// ============================================================================
// 反馈相关接口
// ============================================================================

/** 用户反馈信息 */
export interface IUserFeedBackInformation {
  feedback_type: string;
  feedback_source: number;
  description: string;
  screenshot_urls: string[];
  content_information: string;
}

// ============================================================================
// 用户信息相关 API
// ============================================================================

/** 获取账号信息 */
export const getAccountInfo = async () => {
  const apiClient = await getApiClient();
  const res = await apiClient.get<UserInfoVO>(
    '/user/account/info/',
    {},
    { autoErrorHandle: false },
  );
  return res.data;
};

/** 更新账号信息 */
export const postAccountUpdateInfo = async (data: { acc_name?: string; logo_path?: string }) => {
  const apiClient = await getApiClient();
  return apiClient.post<UserInfoVO>('/user/account/update_info/', data);
};

/** 获取用户手机号账号信息 */
export const postAccountList = async () => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<Account[]>(
    '/user/account30/account_list/',
    {},
    { autoErrorHandle: false },
  );
  return res.data;
};

// 修改团队名称
export const postUserOrgUpdate = async (data: { name: string }) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/org/update/', data);
};

/** 注销账号 */
export const deleteAccount = async (data: { sms_code: string }) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/unregister/', data);
};

/** 注销账号验证码校验 */
export const checkDeleteCode = async (
  data:
    | {
        mobile: string;
        sms_code: string;
      }
    | { email: string; sms_code: string },
) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/check_sms_code/', data);
};

/** 修改账号名称 */
export const changeAccountName = async (data: { login_name: string }) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/change_login_name/', data);
};

/** 更换邮箱 */
export const changeEmail = async (data: { email: string; verify_code: string }) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/international/change_email/', data);
};

/** 更换手机号 */
export const changePhone = async (data: ChangePhoneRequestData) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/change_mobile/', data);
};

/** 更换密码-已登录 */
export const changePasswordLogin = async (data: changePasswordLoginData) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/change_password/', data);
};

/** 邮箱重置密码 */
export const changePasswordEmail = async (data: changePasswordEmailData) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/international/reset_password_by_email/', data);
};

// ============================================================================
// 认证相关 API
// ============================================================================

/** 获取身份信息 */
export const getIdentifyInfo = async () => {
  const apiClient = await getApiClient();
  const res = await apiClient.get<IdentifyInfoResult>('/user/identify/info/');
  return res.data;
};

// ============================================================================
// 登录相关 API
// ============================================================================

/** 通过用户ID获取登录token信息 */
export const postSwitchLogin = async (user_id: number) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<AccountWithSession[]>('/user/account30/switch_login/', {
    user_id,
  });
  return res.data;
};

/** 邮箱登录 */
export const postUserInternationalLoginByEmail = async (data: LoginEmailRequestData) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<LoginInfo[]>('/user/international/login_by_email/', data);
  return res;
};

/** 用户名密码登录 */
export const postUserAccount30PasswordComplexLogin = async (data: AccountLoginData) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<LoginInfo[]>('/user/account30/password_complex_login/', data);
  return res;
};

/** 验证码登录 */
export const postUserAccount30SmsComplexRegisterLogin = async (data: AccountLoginData) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<LoginInfo[]>(
    '/user/account30/sms_complex_register_login/',
    data,
  );
  return res;
};

/** 获取信息脱敏公共key */
export const getUserRsaPublicKey = async () => {
  const apiClient = await getApiClient();
  return apiClient.get<UserRsaPublicKey>('/user/rsa/get_public_key/');
};

// ============================================================================
// 验证码相关 API
// ============================================================================

/** 获取手机验证码 */
export const getRegisterCode = async (mobile: string) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<{ error_code: number; error_reason: string }>(
    '/user/mobile/send_sms_code/',
    { mobile },
  );
  return res;
};

/** 获取邮箱验证码 */
export const getEmailCode = async (email: string) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<{ error_code: number; error_reason: string }>(
    '/user/international/send_code_by_email/',
    {
      email,
    },
  );
  return res;
};

// 转让管理员
export const changeTeamAdmin = async (data: {
  account_id: number;
  sms_code: string;
  mobile?: string;
  email?: string;
}) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post('/user/account30/change_org_admin_account/', data);
  return res;
};

// ============================================================================
// 微信相关 API
// ============================================================================

/** 获取微信绑定状态 */
export const getBindWechatStatus = async (ticket_id: string) => {
  const apiClient = await getApiClient();
  return apiClient.get<IWechatOfficialAccountScanStatus>(
    `/user/account30/bind_wechat_status/?ticket_id=${ticket_id}`,
  );
};

/** 获取绑定微信的二维码 */
export const postBindWechat = async () => {
  const apiClient = await getApiClient();
  return apiClient.post<IWechatOfficialAccountTicket>('/user/account30/bind_wechat/');
};

/** 解绑微信 */
export const postUnbindWechat = async () => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/unbind_wechat/');
};

// ============================================================================
// 飞书相关 API
// ============================================================================
/** 解绑飞书 */
export const postUnbindFeiShu = async () => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/unbind_feishu/');
};

// ============================================================================
// 团队管理相关 API
// ============================================================================

/** 用户列表/搜索 */
export const getMembers = async (params?: IGetOrganizationAccounts) => {
  const apiClient = await getApiClient();
  const res = apiClient.get<PaginatedResponse<Member>>(
    '/user/account/orginzation_account/',
    params,
  );
  return res;
};

/** 注销团队账号 */
export const deleteTeamAccount = async (data: { org_id?: number }) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/org/unregister/', data);
};

/** 更新团队Logo */
export const reqUpdateLogo = async (data: {
  platform_logo_path: string;
  organization_id: number;
}) => {
  const apiClient = await getApiClient();
  return apiClient.put('/user/org/change_logo/', data);
};

/** 创建邀请码 */
export const createInvitation = async () => {
  const apiClient = await getApiClient();
  return apiClient.post<{ token: string }>(
    '/user/account30/gen_invitation_token/',
    {},
    { autoErrorHandle: false },
  );
};

/** 查看邀请码信息 */
export const lookCodeInfo = async (params?: { token: string }) => {
  const apiClient = await getApiClient();
  return apiClient.get<{ account_name: string; organization_name: string }>(
    '/user/account30/invitation_token_info/',
    params,
  );
};

/** 接受邀请 */
export const invitation = async (
  body?: { token: string },
  config?: { autoErrorHandle?: boolean },
) => {
  const apiClient = await getApiClient();
  return apiClient.post<{
    organization_id: number;
    organization_name: string;
    user_id: number;
    username: string;
  }>('/user/account30/invitation/', body, config);
};

/** 团队管理员踢人 */
export const kickMembers = async (data?: KickMemberParams) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/kick/', {
    ...data,
  });
};

/** 团队管理员恢复人 */
export const restoreMembers = async (data?: { user_id: string }) => {
  const apiClient = await getApiClient();
  return apiClient.put('/user/account/enable_account/', data);
};

/** 恢复被踢出的账号 */
export const reinviteMembers = async (data?: { user_id: string }) => {
  const apiClient = await getApiClient();
  return apiClient.put('/user/account/invite_account/', data);
};

/** 编辑团队成员 */
export const editTeamMember = async (data: EditTeamMemberAccount) => {
  const apiClient = await getApiClient();
  return apiClient.put('/user/account30/update_account_info/', data);
};

/** 新增团队成员 */
export const createTeamMember = async (data: TeamMemberAccount) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/account30/create_account/', data);
};

// ============================================================================
// 反馈相关 API
// ============================================================================

/** 保存用户反馈 */
export const postUserFeedBackInformation = async (data: IUserFeedBackInformation) => {
  const apiClient = await getApiClient();
  return apiClient.post('/user/feedback_information/', data);
};

// 消费记录name
export const getProjectNames = async (params?: { user_id?: number }) => {
  const apiClient = await getApiClient();
  return apiClient.get<{ results: string[] }>('/order/consumption/client/project_names/', {
    ...params,
  });
};
// 检查兑换码是否可用
export const postActivityCodeUsing = async (params?: { code: string }) => {
  const apiClient = await getApiClient();
  return apiClient.post<{ results: string[] }>('/activity/code/using/', {
    ...params,
  });
};
/**
 * 团队成员存储使用记录
 * @param
 * @returns
 */
export const postStorageRank = async (params?: Record<string, unknown>) => {
  const apiClient = await getApiClient();
  return apiClient.get<{ results: IRank[] }>('/storage/rank/', {
    ...params,
  });
};

// 首次记录
export const getUserPopUpMemory = async () => {
  const apiClient = await getApiClient();
  const res = await apiClient.get<{ is_first: boolean }>('/user/user_pop_up_memory/');
  return res.data;
};

export const postUserPopUpMemory = async (data: any) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post('/user/user_pop_up_memory/', data);
  return res.data;
};

// 退出登录
export const deleteUserSession = async () => {
  const apiClient = await getApiClient();
  return apiClient.delete('/user/session/');
};
